import type { ECharts } from "echarts/core";
import { useCallback, useRef, useState } from "react";
import type { TimeAxisPanelSpec } from "../../ChartSpec";
import type { GetDisplayInfo } from "../../units/use-get-display-info";
import { getAxisNameAnchor } from "./get-axis-name-anchor";
import type { PanelUnitPickerState } from "./panel-unit-picker";

type AxisNameClickParams = {
    targetType?: string;
    yAxisIndex?: number;
    event?: { target?: unknown };
};

// Y-axis name badges are internal ECharts ZRender canvas nodes — clickable via chart.on('click', 'yAxis') but not DOM-anchorable.
// PanelUnitPicker needs React state + a viewport rect (from getAxisNameAnchor). This hook connects the two.
export function useAxisNameUnitPicker(spec: TimeAxisPanelSpec, getDisplayInfo: GetDisplayInfo) {
    const [pickerState, setPickerState] = useState<PanelUnitPickerState | null>(null);

    // onAxisNameClick is registered once (stable useCallback) — ref keeps axis lookup current as spec edits.
    const specRef = useRef(spec);
    specRef.current = spec;

    // onAxisNameClick must read the latest unit (incl. overrides) without being re-registered on each change.
    const getDisplayInfoRef = useRef(getDisplayInfo);
    getDisplayInfoRef.current = getDisplayInfo;

    // enable() receives the chart instance imperatively; clicks fire later outside React's render cycle.
    const chartRef = useRef<ECharts | null>(null);
    // Viewport anchor math needs the chart container's getBoundingClientRect at click time.
    const containerRef = useRef<HTMLElement | null>(null);

    const onAxisNameClick = useCallback((params: AxisNameClickParams) => {
        if (params.targetType !== "axisName") return;
        const chart = chartRef.current;
        const chartContainer = containerRef.current;
        if (!chart || !chartContainer) return;

        const axis = params.yAxisIndex != null ? specRef.current.axes[params.yAxisIndex] : undefined;
        if (!axis) return;

        // Unknown categories have no selectable units; don't open an empty picker.
        const displayInfo = getDisplayInfoRef.current(axis.unitCategory);
        if (!displayInfo.isKnown) return;

        setPickerState((prev) => {
            if (prev) return null;

            const anchor = getAxisNameAnchor(chart, params.yAxisIndex ?? -1, params.event?.target, chartContainer);
            if (!anchor) return prev;

            return {
                unitCategory: axis.unitCategory,
                currentUnit: displayInfo.unit,
                anchor,
            };
        });
    }, []);

    const enable = useCallback(
        (chart: ECharts, chartContainer: HTMLElement) => {
            if (chartRef.current && chartRef.current !== chart) {
                chartRef.current.off("click", onAxisNameClick);
            }
            chartRef.current = chart;
            containerRef.current = chartContainer;
            chart.on("click", "yAxis", onAxisNameClick);
        },
        [onAxisNameClick],
    );

    const disable = useCallback(() => {
        if (!chartRef.current) return;
        chartRef.current.off("click", onAxisNameClick);
        chartRef.current = null;
        containerRef.current = null;
    }, [onAxisNameClick]);

    return {
        pickerState,
        onPickerStateChange: setPickerState,
        enable,
        disable,
    };
}
